import { useCallback, useEffect, useRef, useState } from 'react';
import type { CoverFrameSource } from '@/lib/cover/buildPreviewStillInputProps';
import type { FrameCandidate, FrameCandidates } from '@/lib/cover/resolveFrameSourceSelection';

export type CoverFrameLoaders = {
  /** The frame as the preview draws it (layout, subtitles, layers). Omitted
   *  when there is no preview to render — that candidate then fails. */
  loadPreviewFrame?: () => Promise<string | null>;
  /** The raw source-video frame, cropped to the export aspect. */
  loadVideoFrame: () => Promise<string | null>;
};

const INITIAL: FrameCandidates = {
  preview: { status: 'loading' },
  video: { status: 'loading' },
};

/** Runs one candidate loader to a settled state. Failures are logged (the
 *  message only) so an "Indisponível" candidate stays diagnosable. */
export async function settleFrameCandidate(
  source: CoverFrameSource,
  loader: (() => Promise<string | null>) | undefined,
): Promise<FrameCandidate> {
  if (!loader) return { status: 'error' };
  try {
    const dataUrl = await loader();
    if (!dataUrl) console.warn(`[cover] ${source} frame unavailable`);
    return dataUrl ? { status: 'ready', dataUrl } : { status: 'error' };
  } catch (error) {
    console.warn(`[cover] ${source} frame failed:`, error instanceof Error ? error.message : error);
    return { status: 'error' };
  }
}

/** Loads both cover-frame candidates in parallel, once per `enabled` turn,
 *  and lets a failed one be retried. Results arriving after unmount or after
 *  `enabled` flips off are dropped. */
export function useCoverFrameCandidates(
  enabled: boolean,
  loaders: CoverFrameLoaders,
): { candidates: FrameCandidates; retry: (source: CoverFrameSource) => void } {
  const [candidates, setCandidates] = useState<FrameCandidates>(INITIAL);
  // Loaders are snapshotted per turn: a re-render (e.g. the playhead moving
  // behind a dialog) must not restart the renders, and a retry must re-run
  // the same request.
  const loadersRef = useRef(loaders);
  const turnRef = useRef(0);

  const run = useCallback((source: CoverFrameSource, turn: number) => {
    const loader = source === 'preview' ? loadersRef.current.loadPreviewFrame : loadersRef.current.loadVideoFrame;
    setCandidates((prev) => ({ ...prev, [source]: { status: 'loading' } }));
    void settleFrameCandidate(source, loader).then((candidate) => {
      if (turnRef.current === turn) setCandidates((prev) => ({ ...prev, [source]: candidate }));
    });
  }, []);

  useEffect(() => {
    if (!enabled) return;
    loadersRef.current = loaders;
    const turn = ++turnRef.current;
    run('preview', turn);
    run('video', turn);
    return () => {
      turnRef.current += 1;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, run]);

  const retry = useCallback((source: CoverFrameSource) => run(source, turnRef.current), [run]);

  return { candidates, retry };
}
